import { compact, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, useWatch } from 'react-hook-form';

import {
  ExtendedTypeaheadQueryOpts,
  useAsyncTypeaheadProps,
} from '@/components/form/baseInputs/BackendTypeaheadSelectInput/hooks/useAsyncTypeaheadProps';
import {
  FormAwareTypeaheadMultiSelectInput,
  FormAwareTypeaheadMultiSelectInputProps,
} from '@/components/form/formAwareInputs/FormAwareTypeaheadMultiSelectInput';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import {
  IntegrationClientKind,
  IntegrationConfigurationAddeparClientType,
} from '@/types/schema';

import { getKindFromProvider } from '../../assetProviderIntegrations.utils';
import {
  AssetIntegrationProviders,
  getIntegrationProviderDisplayName,
} from '../constants';
import {
  GetIntegrationClientsMultipleQuery,
  GetIntegrationClientsMultipleQueryVariables,
  useGetIntegrationClientsMultipleQuery,
} from './graphql/IntegrationClientTypeahead.generated';
import {
  InitialSyncStatus,
  useTrackInitialAssetIntegrationSync,
} from './hooks/useTrackInitialAssetIntegrationSync';
import {
  getBadgeText,
  getHelpTextForStatus,
} from './IntegrationClientTypeahead.util';

export type IntegrationClientsTypeaheadProps<FormShape extends FieldValues> =
  Omit<
    FormAwareTypeaheadMultiSelectInputProps<FormShape>,
    'options' | 'inputValue' | 'onInputChange'
  > & {
    providers: AssetIntegrationProviders[];
    notLoggedIn?: boolean;
  };

export function IntegrationClientsTypeahead<FormShape extends FieldValues>({
  providers,
  notLoggedIn,
  ...typeaheadProps
}: IntegrationClientsTypeaheadProps<FormShape>) {
  const { initialSyncStatus } = useTrackInitialAssetIntegrationSync();

  const providerName = useMemo(() => {
    if (providers.length === 1 && providers[0]) {
      return getIntegrationProviderDisplayName(providers[0]);
    }
    return 'Multiple providers';
  }, [providers]);
  const { createErrorFeedback } = useFeedback();
  const { control } = useFormContext<FormShape>();
  const { integrationConfiguration } = useTenantDetailsContext();

  // Show the type of addepar client if we're importing multiple different ways of modeling clients
  // from addepar
  const shouldShowAddeparKindBadge =
    providers.includes(AssetIntegrationProviders.ADDEPAR) &&
    integrationConfiguration?.addeparClientType ===
      IntegrationConfigurationAddeparClientType.All;

  // Show the provider name if we're exposing multiple different providers
  const shouldShowProviderBadge = providers.length > 1;

  const selectedIntegrationClientIds = useWatch({
    control,
    name: typeaheadProps.fieldName,
  });

  // Once the user has selected an integration client, we "lock" the provider
  // kind so that subsequent backend queries (and therefore the available
  // options) are constrained to that provider. When the selection is cleared
  // we remove the lock.
  const [lockedProviderKind, setLockedProviderKind] =
    useState<IntegrationClientKind | null>(null);

  // Reset the locked provider when the selection is cleared
  useEffect(() => {
    const selectedIds = selectedIntegrationClientIds as string[];
    if (!selectedIds || selectedIds.length === 0) {
      setLockedProviderKind(null);
    }
  }, [selectedIntegrationClientIds]);

  const searchTermToVariables = useCallback(
    (searchTerm: string) => {
      const kinds = lockedProviderKind
        ? [lockedProviderKind]
        : providers.map((p) => getKindFromProvider(p));

      return {
        optionsLike: {
          nameContainsFold: searchTerm,
          kindIn: kinds,
        },
      };
    },
    [providers, lockedProviderKind]
  );

  const getSelectedOptionVariables = useCallback(() => {
    const values = selectedIntegrationClientIds as string[];
    return {
      selectedOptionsLike: {
        idIn: isEmpty(values) ? [''] : compact(values),
        kindNotIn: [IntegrationClientKind.Salesforce],
      },
    };
  }, [selectedIntegrationClientIds]);

  const toOptions: ExtendedTypeaheadQueryOpts<
    string,
    GetIntegrationClientsMultipleQuery,
    GetIntegrationClientsMultipleQueryVariables
  >['toOptions'] = useCallback(
    (data) => {
      // Filter data to only show integration clients from the locked/selected provider
      const selectedProviderKind: IntegrationClientKind | null =
        lockedProviderKind;

      const filteredData = selectedProviderKind
        ? data.filter((client) => client.kind === selectedProviderKind)
        : data;

      return filteredData.map((integrationClient) => {
        const badgeText = getBadgeText(integrationClient, {
          shouldShowAddeparKindBadge: shouldShowAddeparKindBadge,
          shouldShowProviderBadge,
        });

        return {
          value: integrationClient.id,
          display: integrationClient.name,
          badgeText,
        };
      });
    },
    [shouldShowAddeparKindBadge, shouldShowProviderBadge, lockedProviderKind]
  );

  const [asyncTypeaheadProps, { data: typeaheadData }] = useAsyncTypeaheadProps(
    useGetIntegrationClientsMultipleQuery,
    {
      searchTermToVariables,
      getSelectedOptionVariables,
      toOptions,
      skipAutocompleteResetClear: true,
      onError: createErrorFeedback(
        `Error fetching ${providerName} clients. Please refresh the page to try again.`
      ),
    }
  );

  // When we have fetched data, lock provider kind based on the first selected integration client.
  // We don't currently allow people to connect multiple *different* providers as once to the same
  // household, and this is how we protect against that.
  useEffect(() => {
    if (lockedProviderKind) return; // Already locked

    const selectedIds = selectedIntegrationClientIds as string[];
    if (!selectedIds || selectedIds.length === 0) return;

    const firstSelectedClient = typeaheadData?.find((client) =>
      selectedIds.includes(client.id)
    );

    if (firstSelectedClient?.kind) {
      setLockedProviderKind(firstSelectedClient.kind as IntegrationClientKind);
    }
  }, [lockedProviderKind, selectedIntegrationClientIds, typeaheadData]);

  if (providers.length === 0) {
    return null;
  }

  return (
    <FormAwareTypeaheadMultiSelectInput<FormShape>
      {...asyncTypeaheadProps}
      loading={
        asyncTypeaheadProps.loading ||
        initialSyncStatus === InitialSyncStatus.PENDING
      }
      helpText={getHelpTextForStatus({
        status: initialSyncStatus,
        notLoggedIn,
      })}
      placeholder={`Not linked to client`}
      {...typeaheadProps}
      disabled={typeaheadProps.disabled || notLoggedIn}
    />
  );
}
